import type { ChunkProgressReporter } from '../shared/chunk-progress';
import type { EAN } from '../shared/ean';
import type { AvailabilityDecodeResult } from './availability';

/**
 * Everything one availability read decoded.
 *
 * `names` carries the feed's product name for every EAN the read saw, including EANs
 * whose row produced no observation, so a consumer can tell "the feed has this SKU but
 * no vendor listed it" from "the feed does not have this SKU". The name is nullable
 * because the feed leaves it null; consumers decide whether a nameless SKU is shown.
 */
export type AvailabilityReadResult = {
  readonly observations: readonly AvailabilityDecodeResult[];
  readonly names: ReadonlyMap<EAN, string | null>;
};

/** What every failed read yields: no facts, no names, never a partial or faked one. */
export const EMPTY_AVAILABILITY_READ: AvailabilityReadResult = {
  observations: [],
  names: new Map(),
};

/**
 * Reads the distribution family's facts. The implementation is the only code that
 * knows the feed's table, key masking, and payload nesting (PRD D-03, HC-02).
 */
export abstract class AvailabilityRepository {
  /**
   * Every availability fact the feed holds for a brand, across the retained weeks.
   * Paginated: the largest brand holds 28,272 rows.
   */
  abstract readByBrand(brandSlug: string): Promise<AvailabilityReadResult>;

  /**
   * Availability facts for a set of SKUs, narrowed to one brand. EANs are real EANs;
   * translating them to the feed's key form is the implementation's business.
   */
  abstract readByEans(
    eans: readonly EAN[],
    brandSlug: string,
    onProgress?: ChunkProgressReporter
  ): Promise<AvailabilityReadResult>;
}
